/* External */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import cloneDeepL from 'lodash-es/cloneDeep';
import classNames from 'classnames';
import { gtmEvent } from '@dtvgo/gtm-event-report';
import { useGtmContext } from '../GtmContext';
import { gtmDimensions } from '../GtmContext/dimensions/pdp';

/* Components */
import DetailsFrame from '../detailsFrame/NewDetailsFrame';
import BlackoutScreen from '../blackoutScreen/BlackoutScreen';
import SeasonSelector from '../seasonSelector/SeasonSelector';
import GtmComponent from '../GtmContext/GtmComponent';
import DetailsTab from './detailsTab/DetailsTab';
import MoreLikeThis from '../../pdp/moreLikeThis/MoreLikeThis';
import Metadata from '../../pdp/metadata/Metadata';

/* Styles */
import './SeriesDetails.scss';

/* Other */
import {
  formatSeasonAndEpisode,
  formatSeconds,
  joinArrayWithSeparator,
  getUserLanguageFromEnvironmentPublicPdp, isMoreLikeThisActive,
} from '../../utils';
import { api } from '../../state/configureStore';
import { createBackendServicePublic } from '../../services/backend';
import { useIsResponsive } from '../../hooks/useIsResponsive';

/* Constants */
const EPISODES_TAB_KEY = 'episodes';
const MORE_LIKE_THIS_TAB_KEY = 'moreLikeThis';
const DETAILS_TAB_KEY = 'details';

function SeriesDetails({
  assetId,
  onErrorAction,
  intl: { formatMessage },
  isPublic,
  dataPublic,
  regionPublic,
}) {
  const { country } = useParams() || {};
  const isMobile = useIsResponsive();
  const history = useHistory();
  const languageEnvironment = getUserLanguageFromEnvironmentPublicPdp(country);
  const {
    data: serieResults = {},
    isError: isAssetError,
    isLoading: isAssetLoading,
    isSuccess: isAssetSuccess,
  } = !isPublic ? api.useGetSeriesDetailQuery({ assetId }) : dataPublic;
  const { heartbeatOnGoing } = useSelector((state) => state.vrioHeartbeat);

  const { data: { contents: [continueWatchingEpisode] = [] } = {}, isLoading: isContinueWatchingLoading } = !isPublic ? api.useGetContinueWatchingDataQuery({ assetId, assetType: 'series' }, { skip: !isAssetSuccess || heartbeatOnGoing }) : {};
  const { data: seasonDetail = {} } = !isPublic ? api.useGetSeasonDetailQuery(
    { assetId, seasonNumber: continueWatchingEpisode?.episode?.seasonNumber },
    { skip: isContinueWatchingLoading || !continueWatchingEpisode },
  ) : {};
  const [selectedTab, setSelectedTab] = useState(EPISODES_TAB_KEY);
  const [yearsOnAirPublic, setYearsOnAirPublic] = useState('');
  const {
    gtmUserData, gtmContent,
  } = useGtmContext();
  let firstEpisode = {};
  let releaseYear;
  let finaleYear;
  let seasonsCounter;
  const {
    seasons = [],
    genres = [],
    title: serieName,
    vrioAssetId: id = '',
    rating,
  } = serieResults;
  const sortedSeasons = cloneDeepL(seasons).sort(
    (a, b) => Number.parseInt(a.seasonNumber, 10) - Number.parseInt(b.seasonNumber, 10),
  );
  const seasonsWithEpisodes = sortedSeasons.filter(({ hasEpisodes }) => hasEpisodes === true)
    .map((season) => ({
      ...season,
      seasonNumber: Number(season.seasonNumber),
    }));
  const firstSeasonNumber = seasonsWithEpisodes[0]?.seasonNumber;
  const { data: firstSeasonDetail = {} } = !isPublic ? api.useGetSeasonDetailQuery(
    { assetId, seasonNumber: firstSeasonNumber },
    { skip: !firstSeasonNumber && firstSeasonNumber !== 0 },
  ) : {};
  const lastSeasonNumber = seasonsWithEpisodes[seasonsWithEpisodes.length - 1]?.seasonNumber;

  const { data: lastSeasonDetail = {} } = !isPublic ? api.useGetSeasonDetailQuery(
    { assetId, seasonNumber: lastSeasonNumber },
    { skip: seasonsWithEpisodes.length <= 1 },
  ) : {};
  if (seasonsWithEpisodes) {
    const clonedFirstSeasonSortedEpisodes = cloneDeepL(firstSeasonDetail?.episodes);
    const firstSeasonSortedEpisodes = clonedFirstSeasonSortedEpisodes?.sort(
      (a, b) => (a?.episode?.number || 0) - (b?.episode?.number || 0),
    );
    firstEpisode = firstSeasonSortedEpisodes && firstSeasonSortedEpisodes?.[0];
    firstEpisode = {
      ...firstEpisode,
      seasonNumber: firstSeasonNumber,
    };
    releaseYear = firstSeasonSortedEpisodes && firstSeasonSortedEpisodes?.[0]?.vod?.releaseYear;
  }
  if (seasonsWithEpisodes.length > 1) {
    const clonedLastSeasonSortedEpisodes = cloneDeepL(lastSeasonDetail?.episodes);
    const lastSeasonSortedEpisodes = clonedLastSeasonSortedEpisodes?.sort(
      (a, b) => (a?.episode?.number || 0) - (b?.episode?.number || 0),
    );
    const lastEpisodeLastSeason = lastSeasonSortedEpisodes?.length;
    finaleYear = lastSeasonSortedEpisodes?.[lastEpisodeLastSeason - 1]?.vod?.releaseYear;
    seasonsCounter = lastSeasonSortedEpisodes?.[lastEpisodeLastSeason - 1]?.episode?.seasonNumber;
  }

  if (firstSeasonNumber === lastSeasonNumber) {
    const clonedLastSeasonSortedEpisodes = cloneDeepL(firstSeasonDetail?.episodes);
    const lastSeasonSortedEpisodes = clonedLastSeasonSortedEpisodes?.sort(
      (a, b) => (a?.episode?.number || 0) - (b?.episode?.number || 0),
    );
    const lastEpisodeLastSeason = lastSeasonSortedEpisodes?.length;
    finaleYear = lastSeasonSortedEpisodes?.[lastEpisodeLastSeason - 1]?.vod?.releaseYear;
    seasonsCounter = lastSeasonSortedEpisodes?.[lastEpisodeLastSeason - 1]?.episode?.seasonNumber;
  }

  useEffect(() => {
    async function fetchLastSeasonData() {
      const returnLastSeasonData = lastSeasonNumber ? await createBackendServicePublic.get(`/asset/show/${assetId}/seasons/${lastSeasonNumber}`, languageEnvironment, regionPublic) : {};
      const lastEpisodePublic = returnLastSeasonData?.data?.episodes?.length;
      const releaseYearSerie = serieResults?.releaseYear;
      const releaseLastYearSerie = returnLastSeasonData
        ?.data
        ?.episodes
        ?.[lastEpisodePublic - 1]?.releaseYear;
      const yearhOnAirPublic = releaseYearSerie && releaseLastYearSerie && (releaseYearSerie === releaseLastYearSerie ? releaseYearSerie : `${releaseYearSerie} - ${releaseLastYearSerie}`);
      setYearsOnAirPublic(yearhOnAirPublic);
    }
    if (isPublic) {
      fetchLastSeasonData();
    }
  }, [assetId, lastSeasonNumber, serieResults, isPublic, languageEnvironment, regionPublic]);

  useEffect(() => {
    setSelectedTab(EPISODES_TAB_KEY);
  }, [id]);

  function getIconButtonPlayer(asset) {
    if (isPublic) {
      return 'dtv-icon-layers';
    }

    if (asset.shouldDisplayLockIcon) {
      return 'dtv-icon-lock';
    }
    return 'dtv-icon-play';
  }

  if (isAssetError && !isPublic) {
    return (
      <BlackoutScreen
        mainButtonText={formatMessage({ id: 'common.goBack', defaultMessage: 'Regresar' })}
        title={formatMessage({
          id: 'player.errorEntitlementsByStation.title',
          defaultMessage: '¡Lo sentimos!',
        })}
        subtitle={formatMessage({
          id: 'errorPage.serie',
          defaultMessage:
            'Esta serie no está disponible por el momento. Te invitamos a que disfrutes de otro de nuestros contenidos.',
        })}
        onMainButtonClick={onErrorAction}
      />
    );
  }

  if (isAssetError && isPublic) history.replace('/404');

  const tabs = {
    [EPISODES_TAB_KEY]: formatMessage({
      id: 'serie.episodes',
      defaultMessage: 'Episodios',
    }),
    ...((isMoreLikeThisActive && !isPublic) ? {
      [MORE_LIKE_THIS_TAB_KEY]: formatMessage({
        id: 'serie.moreLikeThis',
        defaultMessage: 'Contenidos Relacionados',
      }),
    } : {}),
    [DETAILS_TAB_KEY]: formatMessage({
      id: 'serie.details',
      defaultMessage: 'Detalles',
    }),
  };

  const episodeToPlay = continueWatchingEpisode || firstEpisode || {};
  let description = isPublic ? serieResults.description : episodeToPlay.description;
  const { episode, shouldDisplayLockIcon } = episodeToPlay;
  const formattedSeasonAndEpisode = formatSeasonAndEpisode(
    episode?.seasonNumber,
    episode?.number,
    formatMessage,
  );
  const yearsOnAir = releaseYear && finaleYear && (releaseYear === finaleYear ? releaseYear : `${releaseYear} - ${finaleYear}`);
  const seasonsCounterText = formatMessage(
    { id: 'serie.seasonsCount' },
    {
      seasonsCount: seasonsCounter
        ?? seasonsWithEpisodes[seasonsWithEpisodes.length - 1]?.seasonNumber,
    },
  );

  let episodeName;
  let subtitle;
  let genreMetadata;
  let pdpPlayButtonText;
  let progress;
  if (continueWatchingEpisode) {
    const { vod = {} } = continueWatchingEpisode;
    if (vod) ({ progress } = vod);

    const continueWatchingEpisodeInSeasonDetail = seasonDetail?.episodes?.find(
      ({ vrioAssetId }) => vrioAssetId === continueWatchingEpisode.vrioAssetId,
    );
    description = continueWatchingEpisodeInSeasonDetail?.description || '';

    subtitle = joinArrayWithSeparator([formattedSeasonAndEpisode, continueWatchingEpisodeInSeasonDetail?.title], ' • ');
    episodeName = continueWatchingEpisodeInSeasonDetail?.title;

    const formattedDuration = formatSeconds(continueWatchingEpisodeInSeasonDetail?.vod?.duration);
    const internalRating = continueWatchingEpisodeInSeasonDetail?.rating;
    genreMetadata = (internalRating && formattedDuration) && (
      <Metadata metadataValues={genres} rating={internalRating} showRating />
    );

    if (progress >= (continueWatchingEpisodeInSeasonDetail?.vod?.duration ?? 0) * 0.95) {
      progress = 0;
    }

    pdpPlayButtonText = progress > 0
      ? formatMessage(
        {
          id: 'serie.resume',
          defaultMessage: 'Reanudar',
        },
        { seasonAndEpisode: formattedSeasonAndEpisode },
      )
      : formatMessage(
        {
          id: 'serie.startWatching',
          defaultMessage: 'Comenzar a ver',
        },
        { seasonAndEpisode: formattedSeasonAndEpisode },
      );
  } else {
    genreMetadata = (rating || genres) && (
      <Metadata metadataValues={genres} rating={rating} showRating />
    );

    pdpPlayButtonText = formatMessage(
      {
        id: 'serie.startWatching',
        defaultMessage: 'Comenzar a ver',
      },
      { seasonAndEpisode: formattedSeasonAndEpisode },
    );

    if (isPublic) {
      pdpPlayButtonText = formatMessage({
        id: 'public.pdp.seePlans',
        defaultMessage: 'Conocer planes',
      });
    } else if (shouldDisplayLockIcon) {
      pdpPlayButtonText = formatMessage({
        id: 'public.pdp.playButton',
        defaultMessage: 'Quiero verlo',
      });
    }
    // eslint-disable-next-line unicorn/consistent-destructuring
    episodeName = episodeToPlay?.title;
  }

  const durationMetadata = (yearsOnAir || yearsOnAirPublic) && (
    <Metadata metadataValues={[yearsOnAir || yearsOnAirPublic, seasonsCounterText]} />
  );

  const sendAsset = episodeToPlay;
  const pdpPlayButtonIcon = getIconButtonPlayer(sendAsset);

  const hanleClickSeasonSelector = (tabKey) => {
    setSelectedTab(tabKey);
    gtmEvent({
      hash: gtmDimensions.pdp_series.episodes.hash,
      eventType: gtmDimensions.pdp_series.episodes.eventType,
      dimensions: gtmDimensions.pdp_series.episodes.dimensions,
      userData: gtmUserData,
      data: { ...gtmContent },
    });
  };

  const { vrioAssetId, actionOrigin } = episodeToPlay;

  return (
    <div className="dtv-series-details">
      {!isAssetLoading && vrioAssetId && episodeName && (
        <GtmComponent
          assetToPlay={sendAsset}
          config={{
            title: serieName,
            playSource: 'pdp',
            serieName,
            transmissionType: 'serie',
            contentType: 'episode',
            contentPlayerType: 'serie',
            filterPdp: 'episodes',
            episodeName,
          }}
          hash={gtmDimensions.pdp_series.page_view.hash}
          eventType={gtmDimensions.pdp_series.page_view.eventType}
          dimensions={gtmDimensions.pdp_series.page_view.dimensions}
        />
      )}
      <DetailsFrame
        assetId={assetId}
        assetToPlay={!isPublic ? sendAsset : serieResults}
        description={description}
        isContinueWatchingAsset={!!continueWatchingEpisode}
        isLoading={isAssetLoading || isContinueWatchingLoading}
        genreMetadata={genreMetadata}
        durationMetadata={durationMetadata}
        pdpPlayButtonIcon={pdpPlayButtonIcon}
        pdpPlayButtonText={pdpPlayButtonText}
        progress={progress}
        showMyListButton
        fullHeight
        subtitle={subtitle}
        title={serieName}
        isPublic={isPublic}
      />
      <div
        className="dtv-series-details-tab-selector"
      >
        {Object.keys(tabs).map((tabKey) => {
          const tabText = tabs[tabKey];

          return (
            <span
              className={classNames({
                active: tabKey === selectedTab,
              })}
              key={tabKey}
              onClick={() => hanleClickSeasonSelector(tabKey)}
            >
              {tabText}
            </span>
          );
        })}
      </div>
      {selectedTab === EPISODES_TAB_KEY && (
        <SeasonSelector
          continueWatchingEpisode={continueWatchingEpisode}
          isLoading={isAssetLoading}
          playSource="PDP-Series Episode"
          actionOrigin={actionOrigin}
          seasons={seasonsWithEpisodes}
          assetId={assetId}
          isPublic={isPublic}
          regionPublic={regionPublic}
        />
      )}
      {/* A Feature flag vai ser um pouco diferente aqui, pois essa tela já tinha TABS */}
      {/* Então é só pra remover a Tab da Spideo */}
      {selectedTab === MORE_LIKE_THIS_TAB_KEY && isMoreLikeThisActive && !isPublic && (
        <MoreLikeThis contentId={assetId} key={isMobile} />
      )}
      {selectedTab === DETAILS_TAB_KEY && (
        <DetailsTab
          asset={serieResults}
          isLoading={isAssetLoading || isContinueWatchingLoading}
        />
      )}
    </div>
  );
}

SeriesDetails.propTypes = {
  onErrorAction: PropTypes.func,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  isPublic: PropTypes.bool,
  dataPublic: PropTypes.shape({}),
  regionPublic: PropTypes.string,
};

SeriesDetails.defaultProps = {
  onErrorAction() {},
  isPublic: false,
  dataPublic: {},
  regionPublic: '',
};

export default injectIntl(SeriesDetails);
